import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { Alert, Box, Chip, Grid, Link, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { describeError } from "../../common/rateLimit";
import { Table } from "../../common/table/Table";
import { config } from "../../config";
import { Item } from "../../styles/common";
import { useDebounced } from "../crm/tagging/useDebounced";
import { whatsAppDetailColumns, whatsAppDirectionColumn } from "./whatsAppMessageColumns";
import { WHATSAPP_MESSAGES } from "./whatsapp.queries";

type Direction = "all" | "INBOUND" | "OUTBOUND";

interface Member {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
}

const memberName = ({ firstName, lastName, username }: Member) => `${firstName} ${lastName}`.trim() || username;

/**
 * Every WhatsApp message we sent or received, whoever it was with. The one place
 * a message from a number no account holds can be read.
 */
export const WhatsAppMessages: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(config.rowsPerPage);
  const [direction, setDirection] = useState<Direction>("all");
  const [typed, setTyped] = useState("");
  const search = useDebounced(typed.trim());

  // Moving the search or the filter starts again from the first page.
  const [lastSearch, setLastSearch] = useState(search);
  if (search !== lastSearch) {
    setLastSearch(search);
    setPage(0);
  }

  const { data, loading, error } = useQuery(WHATSAPP_MESSAGES, {
    variables: {
      pagination: { take: rowsPerPage, skip: page * rowsPerPage },
      direction: direction === "all" ? null : direction,
      search: search || null,
    },
    fetchPolicy: "cache-and-network",
  });
  const result = data?.adminWhatsAppMessages;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Item>
            <Typography sx={{ m: 2 }} variant="h4">
              WhatsApp Messages
            </Typography>
          </Item>
        </Grid>
        <Grid item xs={12}>
          <Item sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1}
              sx={{ mb: 1.5 }}
            >
              <TextField
                size="small"
                label="Search"
                placeholder="Number, name or message"
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                sx={{ minWidth: { sm: 300 } }}
              />
              <ToggleButtonGroup
                exclusive
                size="small"
                value={direction}
                aria-label="Which messages"
                onChange={(_, next: Direction | null) => {
                  if (!next) return;
                  setDirection(next);
                  setPage(0);
                }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="INBOUND">Received</ToggleButton>
                <ToggleButton value="OUTBOUND">Sent</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            {error && <Alert severity="error">{describeError(error, "Could not load WhatsApp messages.")}</Alert>}
            {!loading && !error && result?.total === 0 && (
              <Alert severity="info">{search ? "No messages match that search." : "No WhatsApp messages yet."}</Alert>
            )}
            <Table
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              loading={loading}
              rows={result?.messages ?? []}
              total={result?.total ?? 0}
              columns={[
                whatsAppDirectionColumn,
                {
                  skipSorting: true,
                  label: "Person",
                  key: "member",
                  minWidth: "170px",
                  rowRender: (row) =>
                    row.member ? (
                      <Link component={RouterLink} to={`/users/${row.member.id}?tab=whatsapp`} underline="hover" sx={{ fontWeight: 600 }}>
                        {memberName(row.member)}
                      </Link>
                    ) : (
                      <Chip size="small" variant="outlined" label="No account" />
                    ),
                },
                ...whatsAppDetailColumns,
              ]}
            />
          </Item>
        </Grid>
      </Grid>
    </Box>
  );
};
